import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase, Typography } from "@mui/material";
import type { FC } from "react";
import { FONT_SIZE, INK, MOTION } from "../../../theme/tokens";

/** Chevron glyph size — matched to the title's own line box so the header row measures one line. */
const CHEVRON_SIZE = 20;

type FilterSectionHeaderProps = {
	/** Already-translated section title. */
	title: string;
	/** Wired to the section's `aria-labelledby`, so the group announces itself by this title. */
	titleId: string;
	/**
	 * Collapse state. Omit entirely for a static header — no chevron, no button, not focusable, so a
	 * section that cannot collapse never offers a control that does nothing.
	 */
	expanded?: boolean;
	onToggle?: () => void;
	/**
	 * How many filters are active inside this section, rendered as a count beside the title.
	 *
	 * The reason a rail needs it: once a section is collapsed, its selections are invisible, and a
	 * filtered table with no visible cause is the single most confusing state a filter panel has. Omit
	 * or pass 0 to render nothing — a zero badge is noise on every untouched section.
	 */
	activeCount?: number;
	dataTestId?: string;
};

/**
 * The title row shared by every section of a filter rail.
 *
 * One implementation on purpose: `FacetFilterSection` (checkbox facets) and `FilterFieldGroup`
 * (heterogeneous controls) sit next to each other in the same rail, so if each drew its own title
 * they would drift and the rail would read as two different panels stitched together.
 */
export const FilterSectionHeader: FC<FilterSectionHeaderProps> = ({ title, titleId, expanded, onToggle, activeCount, dataTestId }) => {
	const label = (
		<Typography id={titleId} sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY, textAlign: "start" }}>
			{title}
		</Typography>
	);

	// A count only earns its place when it says something — a section with nothing selected is the
	// default state of every section, and badging all of them makes none of them stand out.
	const count =
		activeCount && activeCount > 0 ? (
			<Box
				component="span"
				data-testid={dataTestId ? `${dataTestId}-active-count` : undefined}
				sx={{ ml: 0.75, fontSize: FONT_SIZE.SECONDARY, fontWeight: 700, color: "primary.main" }}
			>
				{activeCount}
			</Box>
		) : null;

	if (expanded === undefined) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", py: 0.75 }} data-testid={dataTestId}>
				{label}
				{count}
			</Box>
		);
	}

	return (
		<ButtonBase
			onClick={onToggle}
			aria-expanded={expanded}
			data-testid={dataTestId}
			sx={{
				width: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				py: 0.75,
				borderRadius: "6px",
				"@media (hover: hover) and (pointer: fine)": {
					"&:hover .filter-section-chevron": { color: INK.SECONDARY },
				},
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
				{label}
				{count}
			</Box>
			<ExpandMoreIcon
				className="filter-section-chevron"
				sx={{
					fontSize: CHEVRON_SIZE,
					color: INK.MUTED,
					transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
					transition: `transform ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				}}
			/>
		</ButtonBase>
	);
};
